import { createSlice } from '@reduxjs/toolkit';

import { status } from 'utils/const';
import { getPerformanceDateTime } from 'utils/dates';
import { thunks } from './thunks';
import { selectors } from './selectors';
import { mediaTypes } from '../../utils/media';
import { calculateFiltersCount } from '../media';

const defaultMediaFilterValues = {
  type: mediaTypes.BOTH,
  dancerSearch: { value: '', label: '' },
  dancers: [],
  classSearch: { value: '', label: '' },
  classes: [],
  eventSearch: { value: '', label: '' },
  events: [],
  routineSearch: { value: '', label: '' },
  routines: [],
  date_start: '',
  date_end: '',
};

const initialState = {
  routineInfo: {},
  loadingRoutineInfoStatus: status.IDLE,

  dancers: {
    routineDancers: [],
    loadingRoutineDancersStatus: status.IDLE,
    currentPage: 1,
  },
  performances: {
    routinePerformances: [],
    loadingRoutinePerformancesStatus: status.IDLE,
    currentPage: 1,
  },
  media: {
    list: [],
    filters: {
      fields: defaultMediaFilterValues,
      updatedFields: 0,
    },
    pagination: {
      total: 0,
      uploadedCount: 20,
    },

    loadingMediaListStatus: status.IDLE,
  },
  audioUrl: '',
  routineDeletingStatus: status.IDLE,
};

const slice = createSlice({
  name: 'routineDetails',
  initialState,
  reducers: {
    resetState(state) {
      Object.assign(state, initialState);
    },
    resetStatus(state, { payload }) {
      state.schedule[payload] = status.IDLE;
    },
    setAudioUrl(state, { payload }) {
      state.audioUrl = payload;
    },
    setTablePage: (state, { payload }) => {
      state.schedule.pagination.current = payload;
    },
    setDancersPage: (state, { payload }) => {
      state.dancers.currentPage = payload;
    },
    setPerformancesPage: (state, { payload }) => {
      state.performances.currentPage = payload;
    },
    resetMediaFilters: (state) => {
      state.media.filters = initialState.media.filters;
    },
    setMediaFilters: (state, { payload }) => {
      state.media.filters.updatedFields = calculateFiltersCount(
        defaultMediaFilterValues,
        payload
      );

      state.media.filters.fields = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(thunks.getRoutineInfo.pending, (state) => {
        state.loadingRoutineInfoStatus = status.PENDING;
      })
      .addCase(thunks.getRoutineInfo.fulfilled, (state, { payload }) => {
        state.routineInfo = payload.data;
        state.audioUrl = payload.data.music_url;
        state.loadingRoutineInfoStatus = status.SUCCESS;
      })
      .addCase(thunks.getRoutineInfo.rejected, (state) => {
        state.loadingRoutineInfoStatus = status.FAIL;
      })

      .addCase(thunks.getRoutineDancers.pending, (state) => {
        state.dancers.loadingRoutineDancersStatus = status.PENDING;
      })
      .addCase(thunks.getRoutineDancers.fulfilled, (state, { payload }) => {
        state.dancers.routineDancers = payload.data.map((d) => ({
          ...d,
          name: `${d.fullName} ${d.isUnderstudy ? '(Understudy)' : ''}`.trim(),
        }));

        state.dancers.loadingRoutineDancersStatus = status.SUCCESS;
      })

      .addCase(thunks.getRoutinePerformances.pending, (state) => {
        state.performances.loadingRoutinePerformancesStatus = status.PENDING;
      })
      .addCase(
        thunks.getRoutinePerformances.fulfilled,
        (state, { payload }) => {
          const { data } = payload;

          state.performances.routinePerformances = data.map((p) => ({
            ...p,
            name: p.event_data.name,
            location: p.event_data.location_formatted,
            dateTime: getPerformanceDateTime(
              p.userLocalDateFormatted,
              p.userLocalTimeFormatted
            ),
          }));

          state.performances.loadingRoutinePerformancesStatus = status.SUCCESS;
        }
      )
      .addCase(thunks.getRoutinePerformances.rejected, (state) => {
        state.performances.loadingRoutinePerformancesStatus = status.FAIL;
      })

      .addCase(thunks.deleteRoutine.pending, (state) => {
        state.routineDeletingStatus = status.PENDING;
      })
      .addCase(thunks.deleteRoutine.fulfilled, (state) => {
        state.routineDeletingStatus = status.SUCCESS;
      })
      .addCase(thunks.deleteRoutine.rejected, (state) => {
        state.routineDeletingStatus = status.FAIL;
      })

      .addCase(thunks.uploadAudio.fulfilled, (state, { payload }) => {
        state.audioUrl = payload.data;
      })
      .addCase(thunks.removeAudio.fulfilled, (state) => {
        state.audioUrl = '';
      })
      .addCase(thunks.getRoutineMedia.pending, (state) => {
        state.media.loadingMediaListStatus = status.PENDING;
      })
      .addCase(thunks.getRoutineMedia.fulfilled, (state, { payload }) => {
        state.media.loadingMediaListStatus = status.SUCCESS;

        state.media.pagination.total = payload.totalFilteredCount;
        state.media.pagination.uploadedCount = payload.limitedCount;
        state.media.list = payload.data;
      })
      .addCase(thunks.getRoutineMedia.rejected, (state) => {
        state.media.loadingMediaListStatus = status.FAIL;
      });
  },
});

const routineDetails = {
  actions: slice.actions,
  thunks,
  selectors,
};

export { routineDetails };
export default slice.reducer;
